import isEqual from "lodash/isEqual";
import { setCookie } from "@litres/utils";
import type {
  CartStatusSerializer,
  IApiResponse,
  IResponse,
  PaginationResponse_CartArtSerializer_,
  DataPaginationResponse_ArtShortSerializer_,
} from "@litres-libs/types";
import type { ThunkDispatch } from "redux-thunk";
import type { AnyAction } from "redux";
import { rtkqApi } from "../api/rtkqApi";

import { TagsType } from "../constants";
import type { RootState } from "../../store";
import { setCustomCookieValue } from "../../toolkit/foundationUser/foundationUserSlice";
import { setHeader } from "../../toolkit/browser/browserSlice";
import { offersApi } from "../offers/offers.api";
import {
  patchArtInCart,
  patchArtNotInCart,
  patchRemoveArtFromList,
} from "../userArts/utils";

export const cartApi = rtkqApi.injectEndpoints({
  endpoints: build => ({
    /**
     * Получить арты в корзине
     */
    getArtsInCart: build.query<
      DataPaginationResponse_ArtShortSerializer_,
      {
        SID?: string;
        availability?: number;
        limit?: number;
        url?: string;
        after?: string;
      }
    >({
      query: ({ SID, availability = 1, url, limit, after }) => ({
        url: url
          ? `/foundation/${url}`
          : `/foundation/api/cart/arts${limit ? `?limit=${limit}` : ""}`,
        headers: SID
          ? {
              "Session-Id": SID,
            }
          : undefined,
        params: { availability, after },
      }),
      transformResponse: (response: PaginationResponse_CartArtSerializer_) => {
        return response.payload;
      },
      providesTags: result =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: TagsType.CART as const,
                id,
              })),
              { type: TagsType.CART, id: "ARTS" },
              ...result.data.map(({ id }) => ({
                type: TagsType.ART_SHORT_LIST as const,
                id,
              })),
              TagsType.ART_SHORT_LIST,
            ]
          : [{ type: TagsType.CART, id: "ARTS" }, TagsType.ART_SHORT_LIST],
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName;
      },
      merge: (currentCache, newResponse, { arg }) => {
        if (arg.after) {
          // при ручной инвалидации кэша арты могут дублироваться, поэтому добавлена фильтрация по id
          const newData = newResponse.data.filter(
            item => !currentCache.data.find(current => current.id === item.id)
          );
          currentCache.data.push(...newData);
        } else {
          currentCache.data = newResponse.data;
        }

        currentCache.pagination = newResponse.pagination;
      },
      forceRefetch({ currentArg, previousArg }) {
        return !isEqual(currentArg, previousArg);
      },
    }),
    /**
     * Получить расчет по корзине
     */
    getCartStatus: build.query<CartStatusSerializer, { SID?: string }>({
      query: ({ SID }) => ({
        url: `/foundation/api/cart/status`,
        headers: SID
          ? {
              "Session-Id": SID,
            }
          : undefined,
      }),
      transformResponse: (response: IApiResponse<CartStatusSerializer>) => {
        return response.payload.data;
      },
      providesTags: [{ type: TagsType.CART, id: "STATUS" }],
    }),
    /**
     * Добавление артов в корзину
     */
    addToCart: build.mutation<IApiResponse<IResponse>, { artIds: number[] }>({
      query({ artIds }) {
        return {
          url: `/foundation/api/cart/arts/add`,
          method: "PUT",
          body: {
            art_ids: artIds,
          },
        };
      },
      invalidatesTags: (_res, _err, { artIds }) => [
        ...artIds.map(id => ({ type: TagsType.MY_BOOKS, id })),
        { type: TagsType.CART, id: "ARTS" },
        { type: TagsType.CART, id: "STATUS" },
        { type: TagsType.OFFER, id: "FOUR-ART-GIFT" },
        { type: TagsType.MY_BOOKS, id: "STATS" },
        TagsType.FOLDERS,
        TagsType.USER_MONETIZATION_DATA,
      ],
      async onQueryStarted({ artIds }, { dispatch, getState, queryFulfilled }) {
        const state = getState() as RootState;
        const entries = rtkqApi.util.selectInvalidatedBy(state, [
          ...artIds.map(id => ({ type: TagsType.MY_BOOKS, id })),
        ]);
        const patches = entries.map(entry =>
          patchArtInCart(artIds, dispatch, entry)
        );
        if (!state.foundationUser.id) {
          addToCart(state, artIds, dispatch);
        }
        try {
          await queryFulfilled;
        } catch (err) {
          console.log(err);
          patches.forEach(patch => {
            patch.undo();
          });
          if (!state.foundationUser.id) {
            removeFromCart(state, artIds, dispatch);
          }
        }
      },
    }),
    /**
     * Удаление артов из корзины
     */
    removeFromCart: build.mutation<
      IApiResponse<IResponse>,
      { artIds: number[] }
    >({
      query({ artIds }) {
        return {
          url: `/foundation/api/cart/arts/remove`,
          method: "PUT",
          body: {
            art_ids: artIds,
          },
        };
      },
      invalidatesTags: (_res, _err, { artIds }) => [
        ...artIds.map(id => ({ type: TagsType.CART, id })),
        ...artIds.map(id => ({ type: TagsType.MY_BOOKS, id })),
        { type: TagsType.CART, id: "ARTS" },
        { type: TagsType.CART, id: "STATUS" },
        { type: TagsType.OFFER, id: "FOUR-ART-GIFT" },
        { type: TagsType.MY_BOOKS, id: "STATS" },
        TagsType.FOLDERS,
        TagsType.USER_MONETIZATION_DATA,
      ],
      async onQueryStarted({ artIds }, { dispatch, getState, queryFulfilled }) {
        const state = getState() as RootState;
        const entries = rtkqApi.util.selectInvalidatedBy(state, [
          ...artIds.map(id => ({ type: TagsType.MY_BOOKS, id })),
        ]);
        const cartEntry = rtkqApi.util
          .selectInvalidatedBy(state, [
            ...artIds.map(id => ({ type: TagsType.CART, id })),
          ])
          .filter(entry => entry.endpointName === "getArtsInCart");
        const patches = entries.map(entry =>
          patchArtNotInCart(artIds, dispatch, entry)
        );
        patches.push(
          ...cartEntry.map(entry =>
            patchRemoveArtFromList(artIds, dispatch, entry)
          )
        );
        patches.push(
          dispatch(
            offersApi.util.updateQueryData(
              "getFourArtOffer",
              undefined,
              cache => {
                Object.assign(cache, {
                  offer_detail: {
                    ...cache.offer_detail,
                    arts: cache.offer_detail.arts.filter(
                      a => !artIds.includes(a.id)
                    ),
                  },
                });
              }
            )
          )
        );
        if (!state.foundationUser.id) {
          removeFromCart(state, artIds, dispatch);
        }
        try {
          await queryFulfilled;
        } catch (err) {
          patches.forEach(patch => {
            patch.undo();
          });
          if (!state.foundationUser.id) {
            addToCart(state, artIds, dispatch);
          }
          console.log(err);
        }
      },
    }),
  }),
});

const removeFromCart = (
  state: RootState,
  artIds: number[],
  dispatch: ThunkDispatch<any, any, AnyAction>
) => {
  const currentBSK = state.foundationUser.BSK ? state.foundationUser.BSK : [];
  const newBSK = currentBSK.filter(id => !artIds.includes(Number(id)));
  const BSK = newBSK.join(",");
  setCookie("BSK_LAST", String(newBSK.length));
  setCookie("BSK", String(BSK));
  dispatch(setCustomCookieValue({ BSK: newBSK }));
  dispatch(setHeader(["Basket", BSK]));
};

const addToCart = (
  state: RootState,
  artIds: number[],
  dispatch: ThunkDispatch<any, any, AnyAction>
) => {
  const currentBSK = state.foundationUser.BSK ? state.foundationUser.BSK : [];
  const newBSK = [
    ...currentBSK,
    ...artIds.map(a => String(a)).filter(a => !currentBSK.includes(a)),
  ];
  const BSK = newBSK.join(",");
  setCookie("BSK_LAST", String(newBSK.length));
  setCookie("BSK", String(BSK));
  dispatch(setCustomCookieValue({ BSK: newBSK }));
  dispatch(setHeader(["Basket", BSK]));
};
